/**
 * URL Shortener for Shopify Collection Filters
 * 
 * Shortens filter URLs by aliasing both param keys and values.
 * Shopify always receives the full original params; only the browser URL is shortened.
 * 
 * HOW TO ADD NEW ALIASES:
 * 1. Add value aliases to VALUE_ALIASES below (short code → full Shopify value)
 * 2. Key aliases are in KEY_ALIASES (short key → full Shopify param name)
 */

// ============================================================
// KEY ALIASES — Short key → Full Shopify param key
// ============================================================

const KEY_ALIASES = {
  "f.ml":   "filter.v.m.custom.variant_custom_filter_list",
  "f.mf":   "filter.v.m.custom.variant_custom_filter",
  "f.dev":  "filter.v.option.device",
  "f.clr":  "filter.v.m.custom.variant_color",
  "p.pt":   "filter.p.product_type",
  "c.dev":  "current.device.choice",
  "c.clr":  "current.color.choice",
};

// ============================================================
// VALUE ALIASES — Short code → Full Shopify filter value
// Auto-generated from store scan, deduplicated & cleaned up.
// ============================================================

const VALUE_ALIASES = {
  // === Product Types (filter.p.product_type) ===
  "acces": "Accessories",
  "bags": "Bags",
  "cable": "Cables",
  "car-acc": "Car Accessories",
  "car-cha": "Car Chargers",
  "car-ext-acc": "Car Exterior Accessories",
  "car-int-acc": "Car Interior Accessories",
  "car-mat": "Car Mats",
  "car-mou-cha": "Car Mount + Chargers",
  "car-mou": "Car Mounts",
  "car-scr-pro": "Car Screen Protectors",
  "card-hol": "Card Holders",
  "carry-cas": "Carrying Case",
  "charg-pad": "Charging Pads",
  "charg-sta": "Charging Stations",
  "clear-cas": "Clear Cases",
  "desk-acc": "Desk Accessories",
  "gamin-acc": "Gaming Accessories",
  "gamin-cas": "Gaming Cases",
  "golf-acc": "Golf Accessories",
  "key-hol": "Key Holders",
  "minim-cas": "Minimalist Cases",
  "phone-acc": "Phone Accessories",
  "phone-str": "Phone Straps",
  "pouch": "Pouches",
  "prote-cas": "Protective Cases",
  "scree-pro": "Screen Protectors",
  "smart-hom": "Smart Home Accessories",
  "stand": "Stands",
  "track-acc": "Tracker Accessories",
  "vr-cas": "VR Cases",
  "wall-cha": "Wall Chargers",
  "watch-acc": "Watch Accessories",
  "watch-ban": "Watch Bands",
  "watch-cas-ban": "Watch Case + Bands",
  "watch-cas": "Watch Cases",
  "wirel-cha": "Wireless Chargers",

  // === Colors (filter.v.m.custom.variant_color) ===
  "beige": "Beige",
  "black": "Black",
  "blue": "Blue",
  "brown": "Brown",
  "clear": "Clear",
  "gold": "Gold",
  "gray": "Gray",
  "green": "Green",
  "gunme": "Gunmetal",
  "navy": "Navy",
  "orang": "Orange",
  "pink": "Pink",
  "purpl": "Purple",
  "red": "Red",
  "silve": "Silver",
  "white": "White",
  "yello": "Yellow",

  // === Tesla Models ===
  "cyber": "Cybertruck",
  "m3-2123": "Model 3 (2021-2023)",
  "m3p-24": "Model 3 Performance (2024+)",
  "m3p-25": "Model 3 Performance (2025+)",
  "m3pr-24": "Model 3 Premium (2024+)",
  "m3pr-25": "Model 3 Premium (2025+)",
  "m3s-24": "Model 3 Standard (2024+)",
  "m3s-25": "Model 3 Standard (2025+)",
  "ms-21": "Model S (2021+)",
  "mx-21": "Model X (2021+)",
  "mx-22": "Model X (2022+)",
  "my-2124": "Model Y (2021-2024)",
  "myj-25": "Model Y Juniper (2025+)",
  "myp-25": "Model Y Performance (2025+)",
  "myp-26": "Model Y Performance (2026+)",
  "mypr-25": "Model Y Premium (2025+)",
  "mys-25": "Model Y Standard (2025+)",

  // === Pack Sizes ===
  "1-pac": "1 Pack",
  "2-pac": "2 Pack",
  "4-pac": "4 Pack",

  // === AirPods / Audio ===
  "ace-pro-2": "Ace Pro 2",
  "airpo": "AirPods",
  "airpo-3": "AirPods 3",
  "airpo-4": "AirPods 4",
  "airpo-max": "AirPods Max",
  "airpo-pro": "AirPods Pro",
  "airpo-pro2": "AirPods Pro 2",
  "airpo-pro3": "AirPods Pro 3",
  "beats-fit": "Beats Fit Pro",
  "beats-bud": "Beats Studio Buds",
  "beats-budp": "Beats Studio Buds+",
  "bose-qc": "Bose QuietComfort Earbuds",
  "bose-qc2": "Bose QuietComfort Earbuds II",
  "bose-spo": "Bose Sport Earbuds",
  "bose-ult": "Bose Ultra Open Earbuds",
  "linkb": "LinkBuds",
  "linkb-s": "LinkBuds S",
  "pwrb-pro2": "Powerbeats Pro 2",
  "wf-xm4": "WF-1000XM4",
  "wf-xm5": "WF-1000XM5",

  // === Apple Watch ===
  "aw-38": "Apple Watch (38mm)",
  "aw-40": "Apple Watch (40mm)",
  "aw-41": "Apple Watch (41mm)",
  "aw-42": "Apple Watch (42mm)",
  "aw-44": "Apple Watch (44mm)",
  "aw-45": "Apple Watch (45mm)",
  "aw-46": "Apple Watch (46mm)",
  "aw-49": "Apple Watch (49mm)",
  "aw1011-42": "Apple Watch 10/11 (42mm)",
  "aw1011-46": "Apple Watch 10/11 (46mm)",
  "aw-ult-49": "Apple Watch Ultra (49mm)",

  // === Apple Vision / Apple TV ===
  "avp": "Apple Vision Pro",

  // === Samsung Galaxy S Series ===
  "gs9": "Galaxy S9",
  "gs9p": "Galaxy S9 Plus",
  "gs10": "Galaxy S10",
  "gs10-5g": "Galaxy S10 5G",
  "gs10p": "Galaxy S10 Plus",
  "gs10e": "Galaxy S10e",
  "gs20": "Galaxy S20",
  "gs20-fe": "Galaxy S20 FE",
  "gs20p": "Galaxy S20 Plus",
  "gs20u": "Galaxy S20 Ultra",
  "gs21": "Galaxy S21",
  "gs21-fe": "Galaxy S21 FE",
  "gs21p": "Galaxy S21 Plus",
  "gs21u": "Galaxy S21 Ultra",
  "gs22": "Galaxy S22",
  "gs22p": "Galaxy S22 Plus",
  "gs22u": "Galaxy S22 Ultra",
  "gs23": "Galaxy S23",
  "gs23-fe": "Galaxy S23 FE",
  "gs23p": "Galaxy S23 Plus",
  "gs23u": "Galaxy S23 Ultra",
  "gs24": "Galaxy S24",
  "gs24-fe": "Galaxy S24 FE",
  "gs24p": "Galaxy S24 Plus",
  "gs24u": "Galaxy S24 Ultra",
  "gs25": "Galaxy S25",
  "gs25-edg": "Galaxy S25 Edge",
  "gs25-fe": "Galaxy S25 FE",
  "gs25p": "Galaxy S25 Plus",
  "gs25u": "Galaxy S25 Ultra",
  "gs26": "Galaxy S26",
  "gs26p": "Galaxy S26 Plus",
  "gs26u": "Galaxy S26 Ultra",

  // === Samsung Galaxy A Series ===
  "ga03s-na": "Galaxy A03s (NA)",
  "ga12": "Galaxy A12",
  "ga13": "Galaxy A13",
  "ga13-5g": "Galaxy A13 5G",
  "ga14-5g": "Galaxy A14 5G",
  "ga14-5g-na": "Galaxy A14 5G (NA)",
  "ga15": "Galaxy A15",
  "ga16": "Galaxy A16",
  "ga16-5g": "Galaxy A16 5G",
  "ga17": "Galaxy A17",
  "ga17-5g": "Galaxy A17 5G",
  "ga21s": "Galaxy A21s",
  "ga23-5g": "Galaxy A23 5G",
  "ga25-5g": "Galaxy A25 5G",
  "ga26-5g": "Galaxy A26 5G",
  "ga32": "Galaxy A32",
  "ga32-5g": "Galaxy A32 5G",
  "ga33-5g": "Galaxy A33 5G",
  "ga34-5g": "Galaxy A34 5G",
  "ga35-5g": "Galaxy A35 5G",
  "ga36-5g": "Galaxy A36 5G",
  "ga37-5g": "Galaxy A37 5G",
  "ga50": "Galaxy A50",
  "ga51": "Galaxy A51",
  "ga52": "Galaxy A52",
  "ga53-5g": "Galaxy A53 5G",
  "ga54-5g": "Galaxy A54 5G",
  "ga55-5g": "Galaxy A55 5G",
  "ga56-5g": "Galaxy A56 5G",
  "ga57-5g": "Galaxy A57 5G",
  "ga71": "Galaxy A71",
  "ga72": "Galaxy A72",
  "ga73-5g": "Galaxy A73 5G",

  // === Samsung Galaxy Note ===
  "gn10p": "Galaxy Note 10 Plus",
  "gn20": "Galaxy Note 20",
  "gn20u": "Galaxy Note 20 Ultra",

  // === Samsung Galaxy Buds ===
  "gbud2": "Galaxy Buds 2",
  "gbud2-pro": "Galaxy Buds 2 Pro",
  "gbud3": "Galaxy Buds 3",
  "gbud3-pro": "Galaxy Buds 3 Pro",
  "gbud4": "Galaxy Buds 4",
  "gbud4-pro": "Galaxy Buds 4 Pro",
  "gbud-cor": "Galaxy Buds Core",
  "gbud-fe": "Galaxy Buds FE",
  "gbud-liv": "Galaxy Buds Live",
  "gbud-pro": "Galaxy Buds Pro",

  // === Samsung Galaxy Z Series ===
  "gzfl4": "Galaxy Z Flip 4",
  "gzfl5": "Galaxy Z Flip 5",
  "gzfl6": "Galaxy Z Flip 6",
  "gzfl7": "Galaxy Z Flip 7",
  "gzfl7-fe": "Galaxy Z Flip 7 FE",
  "gzfo4": "Galaxy Z Fold 4",
  "gzfo5": "Galaxy Z Fold 5",
  "gzfo6": "Galaxy Z Fold 6",
  "gzfo7": "Galaxy Z Fold 7",

  // === Samsung Galaxy Tab ===
  "gtab-a101": "Galaxy Tab A 10.1-inch (2019)",
  "gtab-a11": "Galaxy Tab A11",
  "gtab-a11p": "Galaxy Tab A11+",
  "gtab-a7": "Galaxy Tab A7",
  "gtab-a7l": "Galaxy Tab A7 Lite",
  "gtab-a8": "Galaxy Tab A8",
  "gtab-a9": "Galaxy Tab A9",
  "gtab-a9p": "Galaxy Tab A9+",
  "gtab-s10fe": "Galaxy Tab S10 FE",
  "gtab-s10fep": "Galaxy Tab S10 FE+",
  "gtab-s10u": "Galaxy Tab S10 Ultra",
  "gtab-s10p": "Galaxy Tab S10+",
  "gtab-s11": "Galaxy Tab S11",
  "gtab-s11u": "Galaxy Tab S11 Ultra",
  "gtab-s6": "Galaxy Tab S6",
  "gtab-s6l": "Galaxy Tab S6 Lite",
  "gtab-s7": "Galaxy Tab S7",
  "gtab-s7fe": "Galaxy Tab S7 FE",
  "gtab-s7p": "Galaxy Tab S7+",
  "gtab-s8": "Galaxy Tab S8",
  "gtab-s8u": "Galaxy Tab S8 Ultra",
  "gtab-s8p": "Galaxy Tab S8+",
  "gtab-s9": "Galaxy Tab S9",
  "gtab-s9fe": "Galaxy Tab S9 FE",
  "gtab-s9fep": "Galaxy Tab S9 FE+",
  "gtab-s9u": "Galaxy Tab S9 Ultra",
  "gtab-s9p": "Galaxy Tab S9+",

  // === Samsung Galaxy XCover ===
  "gxc6-pro": "Galaxy XCover 6 Pro",
  "gxc7-pro": "Galaxy XCover 7 Pro",
  "gxc-pro": "Galaxy XCover Pro",

  // === Samsung Watch ===
  "sw-46": "Watch (46mm)",
  "sw3-41": "Watch 3 (41mm)",
  "sw3-45": "Watch 3 (45mm)",
  "sw4-40": "Watch 4 (40mm)",
  "sw4-44": "Watch 4 (44mm)",
  "sw4c-42": "Watch 4 Classic (42mm)",
  "sw4c-46": "Watch 4 Classic (46mm)",
  "sw5-40": "Watch 5 (40mm)",
  "sw5-44": "Watch 5 (44mm)",
  "sw5p-45": "Watch 5 Pro (45mm)",
  "sw6-40": "Watch 6 (40mm)",
  "sw6-44": "Watch 6 (44mm)",
  "sw6c-43": "Watch 6 Classic (43mm)",
  "sw6c-47": "Watch 6 Classic (47mm)",
  "sw7-40": "Watch 7 (40mm)",
  "sw7-44": "Watch 7 (44mm)",
  "sw8-40": "Watch 8 (40mm)",
  "sw8-44": "Watch 8 (44mm)",
  "sw8c-46": "Watch 8 Classic (46mm)",
  "swa2-40": "Watch Active 2 (40mm)",
  "swa2-44": "Watch Active 2 (44mm)",
  "swfe-40": "Watch FE (40mm)",
  "swu-47": "Watch Ultra (47mm)",

  // === iPhone ===
  "ip7": "iPhone 7",
  "ip7p": "iPhone 7 Plus",
  "ip8": "iPhone 8",
  "ip8p": "iPhone 8 Plus",
  "ipx": "iPhone X",
  "ipxr": "iPhone XR",
  "ipxs": "iPhone XS",
  "ipxsm": "iPhone XS Max",
  "ipse3": "iPhone SE (3rd gen)",
  "ip11": "iPhone 11",
  "ip11-pro": "iPhone 11 Pro",
  "ip11-pm": "iPhone 11 Pro Max",
  "ip12": "iPhone 12",
  "ip12-min": "iPhone 12 Mini",
  "ip12-pro": "iPhone 12 Pro",
  "ip12-pm": "iPhone 12 Pro Max",
  "ip13": "iPhone 13",
  "ip13-min": "iPhone 13 Mini",
  "ip13-pro": "iPhone 13 Pro",
  "ip13-pm": "iPhone 13 Pro Max",
  "ip14": "iPhone 14",
  "ip14p": "iPhone 14 Plus",
  "ip14-pro": "iPhone 14 Pro",
  "ip14-pm": "iPhone 14 Pro Max",
  "ip15": "iPhone 15",
  "ip15p": "iPhone 15 Plus",
  "ip15-pro": "iPhone 15 Pro",
  "ip15-pm": "iPhone 15 Pro Max",
  "ip16": "iPhone 16",
  "ip16p": "iPhone 16 Plus",
  "ip16-pro": "iPhone 16 Pro",
  "ip16-pm": "iPhone 16 Pro Max",
  "ip16e": "iPhone 16e",
  "ip17": "iPhone 17",
  "ip17-pro": "iPhone 17 Pro",
  "ip17-pm": "iPhone 17 Pro Max",
  "ip17e": "iPhone 17e",
  "ip-air": "iPhone Air",

  // === iPad ===
  "ipad-102-21": "iPad 10.2-inch (2021)",
  "ipad-109-22": "iPad 10.9-inch (2022)",
  "ipad-11-25": "iPad 11-inch (2025)",
  "ipda-109-22": "iPad Air 10.9-inch (2022)",
  "ipda-11-2426": "iPad Air 11-inch (2024-2026)",
  "ipda-11-2526": "iPad Air 11-inch (2025-2026)",
  "ipda-13-2426": "iPad Air 13-inch (2024-2026)",
  "ipdm-6": "iPad Mini 6",
  "ipdm-7": "iPad Mini 7",
  "ipdp-11-22": "iPad Pro 11-inch (2022)",
  "ipdp-11-24": "iPad Pro 11-inch (2024)",
  "ipdp-11-25": "iPad Pro 11-inch (2025)",
  "ipdp-129-21": "iPad Pro 12.9-inch (2021)",
  "ipdp-129-22": "iPad Pro 12.9-inch (2022)",
  "ipdp-13-24": "iPad Pro 13-inch (2024)",
  "ipdp-13-25": "iPad Pro 13-inch (2025)",

  // === Google Pixel ===
  "px6": "Pixel 6",
  "px6-pro": "Pixel 6 Pro",
  "px6a": "Pixel 6a",
  "px7": "Pixel 7",
  "px7-pro": "Pixel 7 Pro",
  "px7a": "Pixel 7a",
  "px8": "Pixel 8",
  "px8-pro": "Pixel 8 Pro",
  "px8a": "Pixel 8a",
  "px9": "Pixel 9",
  "px9-pro": "Pixel 9 Pro",
  "px9-pf": "Pixel 9 Pro Fold",
  "px9-pxl": "Pixel 9 Pro XL",
  "px9a": "Pixel 9a",
  "px10": "Pixel 10",
  "px10-pro": "Pixel 10 Pro",
  "px10-pf": "Pixel 10 Pro Fold",
  "px10-pxl": "Pixel 10 Pro XL",
  "px10a": "Pixel 10a",
  "pxbud-2a": "Pixel Buds 2a",
  "pxbud-pro": "Pixel Buds Pro",
  "pxbud-pro2": "Pixel Buds Pro 2",
  "px-fold": "Pixel Fold",
  "px-tab": "Pixel Tablet",
  "pxw1": "Pixel Watch",
  "pxw2": "Pixel Watch 2",
  "pxw3-41": "Pixel Watch 3 (41mm)",
  "pxw3-45": "Pixel Watch 3 (45mm)",
  "pxw4-41": "Pixel Watch 4 (41mm)",
  "pxw4-45": "Pixel Watch 4 (45mm)",

  // === Motorola ===
  "motog-5g-24": "Moto G 5G (2024)",
  "motogp-5g-23": "Moto G Power 5G (2023)",
  "motogp-5g-24": "Moto G Power 5G (2024)",
  "motogs-22": "Moto G Stylus (2022)",
  "motogs-5g-23": "Moto G Stylus 5G (2023)",
  "motogs-5g-24": "Moto G Stylus 5G (2024)",
  "moto-edg-24": "Motorola Edge (2024)",
  "moto-edgp-23": "Motorola Edge+ (2023)",
  "moto-raz40u": "Motorola Razr 40 Ultra",
  "moto-razp": "Motorola Razr+",

  // === OnePlus ===
  "op7-pro": "OnePlus 7 Pro",
  "op9-5g": "OnePlus 9 5G",
  "op10-pro": "OnePlus 10 Pro",
  "op11": "OnePlus 11",
  "op12": "OnePlus 12",
  "op13": "OnePlus 13",
  "op13r": "OnePlus 13R",
  "op15": "OnePlus 15",

  // === Nothing ===
  "noth1": "Nothing Phone (1)",
  "noth2": "Nothing Phone (2)",
  "noth2a": "Nothing Phone (2a)",
  "noth3": "Nothing Phone (3)",
  "noth3a": "Nothing Phone (3a)",

  // === Sony Xperia ===
  "xp1-iii": "Xperia 1 III",
  "xp1-iv": "Xperia 1 IV",
  "xp1-v": "Xperia 1 V",
  "xp1-vi": "Xperia 1 VI",
  "xp1-vii": "Xperia 1 VII",
  "xp10-iii": "Xperia 10 III",
  "xp10-iv": "Xperia 10 IV",
  "xp10-v": "Xperia 10 V",
  "xp10-vi": "Xperia 10 VI",
  "xp10-vii": "Xperia 10 VII",
  "xp5-iii": "Xperia 5 III",
  "xp5-iv": "Xperia 5 IV",
  "xp5-v": "Xperia 5 V",

  // === Xiaomi ===
  "xi-mi9l": "Xiaomi Mi 9 Lite",
  "xi-mi10tp": "Xiaomi Mi 10T Pro",
  "xi-mi11l": "Xiaomi Mi 11 Lite",
  "xi-11tp": "Xiaomi 11T Pro",
  "xi-12l": "Xiaomi 12 Lite",
  "xi-12p": "Xiaomi 12 Pro",
  "xi-12tp": "Xiaomi 12T Pro",
  "xi-13": "Xiaomi 13",
  "xi-13l": "Xiaomi 13 Lite",
  "xi-13p": "Xiaomi 13 Pro",
  "xi-13tp": "Xiaomi 13T Pro",
  "xi-14": "Xiaomi 14",
  "xi-14t": "Xiaomi 14T",
  "xi-14tp": "Xiaomi 14T Pro",
  "xi-15": "Xiaomi 15",

  // === Huawei ===
  "hw-hon7x": "Huawei Honor 7X",
  "hw-p20p": "Huawei P20 Pro",
  "hw-p30p": "Huawei P30 Pro",

  // === MacBook / Mac ===
  "mba-13": "MacBook Air 13-inch",
  "mba-15": "MacBook Air 15-inch",
  "mbp-13": "MacBook Pro 13-inch",
  "mbp-13-21": "MacBook Pro 13-inch (2021)",
  "mbp-14": "MacBook Pro 14-inch",
  "mbp-16": "MacBook Pro 16-inch",
  "mbp-16-20": "MacBook Pro 16-inch (2020)",
  "mac-min-m4": "Mac Mini M4",

  // === Surface ===
  "surf-lap-138": "Surface Laptop 13.8-inch (2024)",
  "surf-lap-15": "Surface Laptop 15-inch (2024)",
  "surf-lap3-135": "Surface Laptop 3 13.5-inch",
  "surf-lap3-15": "Surface Laptop 3 15-inch",
  "surf-pro6": "Surface Pro 6",
  "surf-pro8": "Surface Pro 8",
  "surf-pro9": "Surface Pro 9",
  "surf-prox": "Surface Pro X",

  // === HP / Lenovo / Chromebook (if needed) ===
  "hp-pav-x360": "HP Pavilion x360 14 inch",

  // === Gaming ===
  "ns": "Nintendo Switch",
  "ns2": "Nintendo Switch 2",
  "ns-lite": "Nintendo Switch Lite",
  "ns-oled": "Nintendo Switch OLED",
  "ps5": "PlayStation 5",
  "ps-port": "PlayStation Portal",
  "rog-ally": "ROG Ally",
  "rog-allyx-24": "ROG Ally X (2024)",
  "steam-dec": "Steam Deck",
  "leg-go": "Legion Go",
  "leg-go2": "Legion Go Gen 2 (8.8″)",
  "len-leg-go": "Lenovo Legion Go",
  "len-leg-gos": "Lenovo Legion Go S",
  "logi-gcl": "Logitech G CLOUD",
  "logi-gcl2": "Logitech G Cloud",

  // === Meta / VR ===
  "mq3": "Meta Quest 3",
  "mq-pro": "Meta Quest Pro",

  // === GoPro / DJI / Camera ===
  "go-ult": "Go Ultra",
  "hero": "HERO",
  "hero10b": "HERO10 Black",
  "hero11b": "HERO11 Black",
  "hero13b": "HERO13 Black",
  "hero9b": "HERO9 Black",
  "dji-360": "DJI Osmo 360",
  "dji-act4": "DJI Osmo Action 4",
  "dji-act5": "DJI Osmo Action 5",
  "dji-poc": "DJI Osmo Pocket",
  "dji-poc3": "DJI Osmo Pocket 3",

  // === Garmin ===
  "edge-830": "Edge 830",
  "fenix-7": "Fenix 7",
  "fenix-7s": "Fenix 7S",
  "fenix-7x": "Fenix 7X",
  "fenix-8-43": "Fenix 8 (43mm)",
  "fenix-8-47": "Fenix 8 (47mm)",
  "fore-955": "Forerunner 955",
  "insti": "Instinct",
  "insti-2": "Instinct 2",
  "insti-2s": "Instinct 2S",
  "insti-2xs": "Instinct 2X Solar",
  "x4": "X4",

  // === Fitbit ===
  "fitb-v3": "Fitbit Versa 3",
  "fitb-v4": "Fitbit Versa 4",

  // === Golf Rangefinders ===
  "bt-s3max": "Blue Tees Golf Series 3 Max",
  "bush-px3": "Bushnell Pro X3",
  "bush-pxe": "Bushnell Pro XE",
  "bush-v5s": "Bushnell Tour V5 Shift",
  "bush-v6": "Bushnell Tour V6",
  "bush-v6s": "Bushnell Tour V6 Shift",
  "calla-300": "Callaway 300 Pro Laser",

  // === Smart Home ===
  "eero-6": "Eero 6",
  "eero-7": "Eero 7",
  "starl-g3": "Starlink Gen 3",
  "starl-min": "Starlink Mini",

  // === Car Screen Protectors (auto models) ===
  "audi-q4": "Audi Q4 e-tron 11.6-inch",
  "audi-et": "Audi e-tron 11.5-inch",
  "bmw-mot65": "BMW Motorrad 6.5-inch",
  "chev-bolt": "Chevrolet Bolt EV 10.2-inch",
  "chev-equi": "Chevrolet Equinox 8-inch",
  "chev-mali": "Chevrolet Malibu 8-inch",
  "chev-silv": "Chevrolet Silverado 13.4-inch",
  "chev-sub": "Chevrolet Suburban 10.2-inch",
  "dodge-ram": "Dodge Ram 12-inch",
  "ford-bro12": "Ford Bronco 12-inch",
  "ford-bro8": "Ford Bronco 8-inch",
  "ford-esc": "Ford Escape 8-inch",
  "ford-f150-12": "Ford F-150 12-inch",
  "ford-f150-155": "Ford F-150 15.5-inch",
  "ford-mav13": "Ford Maverick 13.2-inch",
  "ford-mav8": "Ford Maverick 8-inch",
  "hyun-pal10": "Hyundai Palisade 10.25-inch",
  "hyun-pal12": "Hyundai Palisade 12.3-inch",
  "hyun-tuc": "Hyundai Tucson 10.25-inch",
  "jeep-glad": "Jeep Gladiator (2024)",
  "jeep-gc-l": "Jeep Grand Cherokee L 10.1-inch",
  "jeep-wra24": "Jeep Wrangler (2024)",
  "jeep-wra12": "Jeep Wrangler 12.3-inch",
  "jeep-wra8": "Jeep Wrangler 8.4-inch",
  "lexus-rx": "Lexus RX 14-inch",
  "merc-a": "Mercedes A-Class",
  "merc-c": "Mercedes C-Class",
  "merc-e": "Mercedes E-Class",
  "merc-s": "Mercedes S-Class",
  "mini-coop": "Mini Cooper 8.8-inch",
  "pole-2": "Polestar 2 11.2-inch",
  "riv-r1sr1t": "Rivian R1S & R1T",
  "riv-r1s-22": "Rivian R1S (2022-2025)",
  "toy-4run": "Toyota 4Runner (2025)",
  "toy-cam12": "Toyota Camry 12.3-inch (2025)",
  "toy-rav": "Toyota RAV4",
  "toy-rav10": "Toyota RAV4 10.5-inch (2023)",
  "toy-rav8": "Toyota RAV4 8-inch",
  "toy-rav8-23": "Toyota RAV4 8-inch (2023)",
  "toy-tac": "Toyota Tacoma (2024-2025)",
  "toy-bz4x": "Toyota bZ4X 12.3-inch (2023)",
  "vw-golf": "VW Golf 10-inch",
  "vw-id4-10": "VW ID.4 10-inch",
  "vw-id4-12": "VW ID.4 12-inch",
  "vw-tigu": "VW Tiguan 8-inch",
  "volvo-xc60": "Volvo XC60 8.7-inch",

  // === Misc / Accessories ===
  "klas-p2": "Klasden Pouch 2",
  "klas-pr": "Klasden Pouch Renewed",
  "l-xl": "L-XL",
  "m-l": "M-L",
  "small-2": "Small (2)",
  "small-2xl": "Small (2) + XL (1)",
  "large-2": "Large (2)",

  // === LD201 Series ===
  "ld-s1": "LD201-S1",
  "ld-s2": "LD201-S2",
  "ld-s3": "LD201-S3",
  "ld-s4": "LD201-S4",
  "ld-s6": "LD201-S6",
  "ld-s7": "LD201-S7",
  "ld-s8": "LD201-S8",
  "ld-s9": "LD201-S9",
  "ld-s10": "LD201-S10",
};


// ============================================================
// INTERNAL — Build reverse lookup maps (do not edit)
// ============================================================

const REVERSE_KEY_ALIASES = {};
for (const [short, full] of Object.entries(KEY_ALIASES)) {
  REVERSE_KEY_ALIASES[full] = short;
}

const REVERSE_VALUE_ALIASES = {};
for (const [short, full] of Object.entries(VALUE_ALIASES)) {
  REVERSE_VALUE_ALIASES[full.toLowerCase()] = short;
}


// ============================================================
// PUBLIC API
// ============================================================

function shortenSearchParams(fullSearchParams) {
  if (!fullSearchParams) return fullSearchParams;
  const params = new URLSearchParams(fullSearchParams);
  const shortened = new URLSearchParams();
  for (const [key, value] of params.entries()) {
    const shortKey = REVERSE_KEY_ALIASES[key] || key;
    const shortValue = REVERSE_VALUE_ALIASES[value.toLowerCase()] || value;
    shortened.append(shortKey, shortValue);
  }
  return shortened.toString();
}

function expandSearchParams(shortSearchParams) {
  if (!shortSearchParams) return shortSearchParams;
  const params = new URLSearchParams(shortSearchParams);
  const expanded = new URLSearchParams();
  for (const [key, value] of params.entries()) {
    const fullKey = KEY_ALIASES[key] || key;
    const fullValue = VALUE_ALIASES[value] || value;
    expanded.append(fullKey, fullValue);
  }
  return expanded.toString();
}

function hasShortParams() {
  const search = window.location.search;
  for (const shortKey of Object.keys(KEY_ALIASES)) {
    if (search.includes(shortKey + "=")) return true;
  }
  return false;
}

function handleShortUrlOnLoad(renderPageCallback) {
  if (!hasShortParams()) return;
  const shortParams = window.location.search.substring(1);
  const fullParams = expandSearchParams(shortParams);
  history.replaceState({ searchParams: fullParams }, "", `${window.location.pathname}?${shortParams}`);
  if (renderPageCallback) {
    renderPageCallback(fullParams, false);
  }
}

window.SQUrlShortener = {
  shortenSearchParams,
  expandSearchParams,
  hasShortParams,
  handleShortUrlOnLoad,
  KEY_ALIASES,
  VALUE_ALIASES,
  REVERSE_KEY_ALIASES,
  REVERSE_VALUE_ALIASES,
};




// ============================================================
// AUTO-REDIRECT: Expand short URLs on page load (before render)
// This runs immediately when the script is loaded synchronously.
// ============================================================
(function() {
  var s = window.location.search;
  if (!s || !hasShortParams()) return;

  var fullParams = expandSearchParams(s.substring(1));

  // Only redirect if something actually changed
  if (fullParams !== s.substring(1)) {
    window.location.replace(window.location.pathname + '?' + fullParams);
  }
})();